import React from "react";
import {
  ChevronLeft,
  CheckCircle2,
  Microscope,
  HeartHandshake,
  Sprout,
  BookOpen,
  ArrowRight,
} from "lucide-react";

interface AboutPageProps {
  onBack: () => void;
  onStart: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onBack, onStart }) => {
  return (
    <div className="bg-white min-h-screen pt-14 pb-24 font-sans text-slate-900">
      {/* Hero Section */}
      <div className="bg-slate-50 border-b border-slate-100 px-6 py-16 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <button
            onClick={onBack}
            className="inline-flex items-center text-xs font-bold text-slate-400 hover:text-orange-600 uppercase tracking-widest mb-8 transition-colors"
          >
            <ChevronLeft size={14} className="mr-1" /> Back to Home
          </button>

          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
            A Smarter Way to Choose <br className="hidden md:block" /> Skincare
            for Your Baby
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Choosing the right skincare product for a baby isn’t easy. BabySkinQ
            solves this with a simple idea:
            <span className="font-bold text-slate-900 block mt-2">
              Use science-based questions to match babies with the right
              skincare products.
            </span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Section 1: The Quiz */}
        <div className="flex flex-col md:flex-row gap-12 items-start mb-20">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
            <Microscope size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              A Clinical-Style Skin Matching Quiz
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Instead of browsing endless product pages, parents start with a
              short, 5-step quiz. These inputs help BabySkinQ understand not
              just <em>what</em> the issue is, but <em>why</em> it might be
              happening.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Baby’s age",
                "Main skin concern",
                "Triggering factors",
                "Current regimen",
                "Parental priorities",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-sm font-bold text-slate-700 bg-slate-50 px-4 py-3 rounded-lg border border-slate-100"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>{" "}
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Section 2: Strategy */}
        <div className="flex flex-col md:flex-row gap-12 items-start mb-20">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              From Symptoms to Skincare Strategy
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              After completing the quiz, parents receive a clear summary of
              their baby’s skin condition and a curated list of suitable
              products. Instead of generic recommendations, we focus on:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Hypoallergenic formulas",
                "Clinically tested products",
                "Fragrance-free options",
                "Skin barrier–friendly ingredients",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-4 border border-slate-100 rounded-xl hover:border-slate-300 transition-colors"
                >
                  <CheckCircle2 size={18} className="text-green-500" />
                  <span className="font-semibold text-slate-800 text-sm">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 3: Education */}
        <div className="flex flex-col md:flex-row gap-12 items-start mb-20">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
            <BookOpen size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              More Than Product Recommendations
            </h2>
            <p className="text-slate-600 mb-4 leading-relaxed">
              BabySkinQ is not just a recommendation tool. We provide{" "}
              <strong>educational skincare content</strong> to help parents
              build long-term habits.
            </p>
            <p className="text-slate-500 text-sm italic border-l-2 border-slate-200 pl-4">
              "The goal is to reduce guesswork and help parents make confident,
              informed choices."
            </p>
          </div>
        </div>

        {/* Section 4: Future */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-12 text-white mb-16 relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6 text-orange-400">
              <Sprout size={24} />
              <span className="text-xs font-bold uppercase tracking-widest">
                Our Vision
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              A Platform That Grows With Parents’ Needs
            </h2>
            <p className="text-slate-300 mb-8 leading-relaxed max-w-2xl">
              While BabySkinQ currently curates trusted third-party products,
              the long-term plan is to introduce{" "}
              <strong>our own baby skincare line</strong>.
              <br />
              <br />
              By understanding real skin concerns and usage patterns, we are
              building the blueprint for products that are data-informed,
              problem-focused, and aligned with real parental needs.
            </p>
          </div>
          {/* Decorative circle */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-slate-800 rounded-full opacity-50 blur-3xl"></div>
        </div>

        {/* Closing / CTA */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white border border-slate-100 rounded-full shadow-sm mb-6">
            <HeartHandshake size={32} className="text-orange-500" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Why BabySkinQ Matters
          </h2>
          <p className="text-slate-600 mb-10 text-lg">
            For parents, baby skincare isn’t about beauty. It’s about comfort,
            safety, and peace of mind. We help answer the critical question:{" "}
            <br />
            <span className="font-bold text-slate-900">
              “What’s the right skincare for my baby?”
            </span>
          </p>

          <button
            onClick={onStart}
            className="px-10 py-4 bg-orange-600 text-white text-lg font-bold rounded-xl shadow-xl shadow-orange-200 hover:bg-orange-700 hover:shadow-orange-300 hover:-translate-y-1 transition-all flex items-center justify-center gap-2 mx-auto"
          >
            Find Your Baby's Match <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
